/**
 * CANYON — desert-canyon realism pass (2026-11-04, owner: "improve each map",
 * "realistic", "get inspired by existing models, don't copy them").
 *
 * The canyon is a sand floor walled in by four 12 m rock walls, eleven rock
 * formations and two 5 m mesas — all plain axis-aligned boxes, so it read as a
 * toy box room wearing a brick texture. Inspiration (not copies): Colorado
 * Plateau / slot-canyon photography and the usual photoscanned "desert cliff"
 * kits — layered sandstone with hard caprock ledges over undercut soft beds,
 * vertical fluting, dark desert-varnish streaks hanging from the rim, a talus
 * apron of broken rock at every foot, wind-blown sand banked against the
 * walls, bunchgrass and sagebrush, and tiered mesas stepping up behind the rim.
 *
 * What it adds (all built ONCE at load, ≤ 8 draw calls even after the scanned
 * rocks swap in):
 *  1. ROCK SHELLS — every rock box / mesa / wall gets a sculpted skin hugging
 *     its exposed faces: displacement is ALWAYS outward (so the box never
 *     shows through) and never more than SHELL_MAX (sides) / TOP_MAX (tops).
 *     Strata, caprock ledges, flutes, varnish and contact AO live in the
 *     geometry + vertex colour; the micro detail is the photo rock family.
 *  2. BACKDROP — tiered sandstone cliffs rising behind the rim, OUTSIDE the
 *     walls' outer faces (the playable volume never touches it).
 *  3. SAND DRIFTS — low wind-banked sand against wall and rock feet, wearing
 *     the floor's own photo sand, ≤ DRIFT_MAX high.
 *  4. TALUS — broken rock at the feet, an InstancedMesh tagged `propKind:
 *     "rock"` so mapProps.ts swaps in the real photoscanned rocks when they
 *     load (procedural fallback otherwise), every piece ≤ RUBBLE_MAX_H tall.
 *  5. DRY VEGETATION — bunchgrass tufts and sagebrush (wind-swayed), soft,
 *     walk-through, ≤ VEG_MAX_H, never on a spawn.
 *
 * `canyonParts` is the pure geometry/layout step (three.js maths only, no
 * DOM / textures) so `agent/bench-extras-canyon.mjs` checks the collision
 * contract against the real shared boxes.
 */
import * as THREE from "three";
import { mergeGeometries, mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { MapBox, MapDef } from "@neon-purge/shared";
import type { MapExtrasContext } from "../mapExtras.js";
import { surfaceMaps, worldProjectUV } from "../mapTextures.js";
import { applyWind, uprightSway, windGeometry } from "../wind.js";

export const mapIds = ["canyon"];

// ---------------------------------------------------------------------------
// contract numbers (the bench quotes these)
/** rock skin: outward displacement range on vertical faces (contract ≤ 0.26) */
export const SHELL_MIN = 0.02;
export const SHELL_MAX = 0.24;
/** rock skin on standable TOP faces: a few cm of relief, never a new ledge */
export const TOP_MIN = 0.01;
export const TOP_MAX = 0.06;
/** side skins start this far below the floor (buried base, no seam) */
export const SINK = 0.08;
/** metres per UV unit on the skins (the scan repeats every 3.4x this) */
const SHELL_UV = 1.1;
/** sand drifts never rise higher than this above the floor */
export const DRIFT_MAX = 0.28;
/** talus pieces: worst-case top above the floor (procedural AND scanned) */
export const RUBBLE_MAX_H = 0.5;
/** vegetation height cap — knee-high soft stuff, never cover */
export const VEG_MAX_H = 0.95;
/** nothing grows / piles within this of a spawn point */
export const SPAWN_CLEAR = 1.3;
/** the mapProps scan swap: SEAT.rock scale/embed + worst normalised scan
 *  height (rock_07 ≈ 0.9, measured from the GLB accessors; 1.1 for margin) */
export const SCAN_SCALE = 1.5, SCAN_EMBED = 0.18, SCAN_MAX_H = 1.1;
/** procedural talus unit rock: radius 1, half-height PROC_HALF_H; centre is
 *  placed at ground + sy*PROC_LIFT (mapProps reads `propLift`) */
export const PROC_HALF_H = 0.62, PROC_LIFT = 0.3;

// ---------------------------------------------------------------------------
// deterministic noise (no Math.random: the layout is stable per load + bench)
function hashI(a: number, b: number, c: number, s: number): number {
  let h = Math.imul(a | 0, 374761393) ^ Math.imul(b | 0, 668265263) ^ Math.imul(c | 0, 1440662683) ^ Math.imul(s | 0, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
const fade = (t: number): number => t * t * (3 - 2 * t);
/** 3D value noise in [0,1). */
function vnoise(x: number, y: number, z: number, s = 0): number {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
  const xf = fade(x - xi), yf = fade(y - yi), zf = fade(z - zi);
  const l = (a: number, b: number, t: number): number => a + (b - a) * t;
  const c = (dx: number, dy: number, dz: number): number => hashI(xi + dx, yi + dy, zi + dz, s);
  return l(
    l(l(c(0, 0, 0), c(1, 0, 0), xf), l(c(0, 1, 0), c(1, 1, 0), xf), yf),
    l(l(c(0, 0, 1), c(1, 0, 1), xf), l(c(0, 1, 1), c(1, 1, 1), xf), yf),
    zf,
  );
}
function fbm(x: number, y: number, z: number, s: number, oct = 3): number {
  let sum = 0, amp = 0.5, f = 1, norm = 0;
  for (let o = 0; o < oct; o++) { sum += vnoise(x * f, y * f, z * f, s + o * 17) * amp; norm += amp; amp *= 0.5; f *= 2.03; }
  return sum / norm;
}
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (a: number, b: number, t: number): number => fade(clamp01((t - a) / (b - a)));
function mulberry(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
/** exact-at-ends linear step (shared edges of two face grids must coincide bit-for-bit) */
const lin = (a: number, b: number, i: number, n: number): number => (i === 0 ? a : i === n ? b : a + ((b - a) * i) / n);

// ---------------------------------------------------------------------------
// palette — layered sandstone (sRGB hex → linear via THREE.Color)
const STRATA = ["#8a4228", "#a4532f", "#b8683c", "#9a5a3e", "#c47f4e", "#cf9a66", "#a14e2e", "#7a4634"].map((h) => new THREE.Color(h));
const CREAM = new THREE.Color("#dcc095");
const VARNISH = new THREE.Color("#3b2a22");
const DUST = new THREE.Color("#cfae7a");
const FOOT = new THREE.Color("#7c5a40");
/** the photo rock albedo averages ~0.57 linear: lift the tint so the product
 *  lands on the box palette's brightness */
const ROCK_GAIN = 1.3;

const tmpC = new THREE.Color(), tmpC2 = new THREE.Color();
/** strata colour at a world point (also used by the backdrop). */
function strataColor(x: number, y: number, z: number, out: THREE.Color): THREE.Color {
  const warp = (vnoise(x * 0.035, 0.5, z * 0.035, 7) - 0.5) * 1.4;
  const yy = y + warp;
  const band = Math.floor(yy / 0.62 + 0.18 * Math.sin(yy * 0.9));
  // the palette drifts SLOWLY with height (formations, not a candy stripe);
  // each bed only nudges brightness/hue around it
  const g = vnoise(0.5, yy * 0.4, 0.5, 9) * (STRATA.length - 1);
  const k0 = Math.floor(g);
  out.copy(STRATA[k0]).lerp(STRATA[Math.min(STRATA.length - 1, k0 + 1)], g - k0);
  const hb = hashI(band, 3, 1, 11);
  out.lerp(STRATA[Math.floor(hashI(band, 5, 2, 17) * STRATA.length)], 0.45);
  out.multiplyScalar(0.8 + 0.36 * hb);
  if (hb > 0.86) out.lerp(CREAM, 0.3); // the odd bleached bed
  // upper beds bleach toward cream (Navajo-over-Kayenta look), noisy boundary
  const hi = smooth(5, 16, y + (vnoise(x * 0.08, 0, z * 0.08, 5) - 0.5) * 3);
  out.lerp(CREAM, hi * 0.35);
  // broad lateral tone drift so the same bed is not one flat colour for 90 m
  const drift = 0.88 + 0.24 * fbm(x * 0.07, y * 0.05, z * 0.07, 21, 2);
  return out.multiplyScalar(drift);
}

// ---------------------------------------------------------------------------
// box classification
export interface Aabb { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number }
export const aabbOf = (b: MapBox): Aabb => ({
  x0: b.p[0] - b.s[0] / 2, x1: b.p[0] + b.s[0] / 2,
  y0: b.p[1] - b.s[1] / 2, y1: b.p[1] + b.s[1] / 2,
  z0: b.p[2] - b.s[2] / 2, z1: b.p[2] + b.s[2] / 2,
});
const contains = (o: Aabb, i: Aabb): boolean =>
  i.x0 >= o.x0 - 1e-6 && i.x1 <= o.x1 + 1e-6 && i.y0 >= o.y0 - 1e-6 && i.y1 <= o.y1 + 1e-6 && i.z0 >= o.z0 - 1e-6 && i.z1 <= o.z1 + 1e-6;

/** A dressed plane of a box: axis 0/1/2, sign ±1. */
export interface Plane { axis: 0 | 1 | 2; sign: 1 | -1 }
/** a face rectangle where decor.ts hangs a banner 0.16 m off the wall: the
 *  skin flattens to ≤ BANNER_D there so the cloth never sinks into rock */
export interface Zone { axis: 0 | 2; sign: 1 | -1; u0: number; u1: number; y0: number; y1: number }
export interface Dressed { index: number; box: Aabb; planes: Plane[]; wall: boolean; zones: Zone[] }
export const BANNER_D = 0.1;

/** Mirror of decor.ts `bannerSpots` geometry (without its random ±0.3 m
 *  jitter, which the zone margin covers). The bench cross-checks it against
 *  the real function. */
export function bannerZones(map: MapDef): Map<number, Zone[]> {
  const { min, max } = map.bounds;
  const cx = (min[0] + max[0]) / 2, cz = (min[2] + max[2]) / 2;
  const out = new Map<number, Zone[]>();
  map.boxes.forEach((b, i) => {
    const [sw, sh, sd] = b.s;
    const top = b.p[1] + sh / 2, base = b.p[1] - sh / 2;
    if (!(sh >= 4.5 && Math.max(sw, sd) >= 6 && Math.min(sw, sd) >= 0.9 && top >= 4 && top <= 44 && base <= 4)) return;
    const dx = cx - b.p[0], dz = cz - b.p[2];
    const useX = Math.abs(dx) * sd > Math.abs(dz) * sw;
    const sign = ((useX ? Math.sign(dx) : Math.sign(dz)) || 1) as 1 | -1;
    const faceLen = useX ? sd : sw;
    const bh = Math.max(1.8, Math.min(3.6, sh * 0.45)), bw = bh * 0.49;
    const py = Math.max(Math.min(top - 1.0, base + 9.5), base + bh + 0.4);
    const count = Math.max(1, Math.min(3, Math.floor(faceLen / 7)));
    const zs: Zone[] = [];
    for (let k = 0; k < count; k++) {
      const t = (k + 1) / (count + 1) - 0.5;
      const along = (useX ? b.p[2] : b.p[0]) + t * (faceLen - bw - 1.2);
      const m = bw / 2 + 0.7;
      zs.push({ axis: useX ? 0 : 2, sign, u0: along - m, u1: along + m, y0: py - bh - 0.45, y1: py + 0.45 });
    }
    out.set(i, zs);
  });
  return out;
}

/** is this a natural-rock tint (warm, saturated) — not a grey cover crate? */
function earthy(colour: string | undefined): boolean {
  if (!colour) return false;
  const hsl = { h: 0, s: 0, l: 0 };
  new THREE.Color(colour).getHSL(hsl, THREE.SRGBColorSpace);
  return hsl.s > 0.18 && hsl.h * 360 >= 8 && hsl.h * 360 <= 55;
}

export interface CanyonClass {
  floorTop: number;
  /** the playable rectangle = inner faces of the perimeter walls */
  inner: { x0: number; x1: number; z0: number; z1: number };
  /** the outer faces of the perimeter walls (backdrop starts here) */
  outer: { x0: number; x1: number; z0: number; z1: number };
  dressed: Dressed[];
  /** every solid non-floor collider (for placement exclusion) */
  solids: Aabb[];
  /** every solid collider incl. the floor (dressing-strip keep test) */
  all: Aabb[];
}

export function classify(map: MapDef): CanyonClass {
  const boxes = map.boxes.map((b, i) => ({ b, i, a: aabbOf(b) }));
  const solid = boxes.filter((e) => e.b.tag !== "water");
  // floor = the biggest-footprint box whose top is the lowest walkable plane
  let floor = solid[0];
  for (const e of solid) if (e.b.s[0] * e.b.s[2] > floor.b.s[0] * floor.b.s[2]) floor = e;
  const floorTop = floor.a.y1;
  const rest = solid.filter((e) => e !== floor);
  const walls = rest.filter((e) => e.b.s[1] >= 8 && Math.min(e.b.s[0], e.b.s[2]) <= 3 && Math.max(e.b.s[0], e.b.s[2]) >= 40);
  const zones = bannerZones(map);
  const inner = { x0: -Infinity, x1: Infinity, z0: -Infinity, z1: Infinity };
  const outer = { x0: -Infinity, x1: Infinity, z0: -Infinity, z1: Infinity };
  const dressed: Dressed[] = [];
  for (const w of walls) {
    const thinX = w.b.s[0] < w.b.s[2];
    const axis = thinX ? 0 : 2;
    const c = w.b.p[axis];
    const sign: 1 | -1 = c > 0 ? -1 : 1; // inner face points at the centre
    if (axis === 0) {
      if (c < 0) { inner.x0 = Math.max(inner.x0, w.a.x1); outer.x0 = Math.max(outer.x0, w.a.x0); }
      else { inner.x1 = Math.min(inner.x1, w.a.x0); outer.x1 = Math.min(outer.x1, w.a.x1); }
    } else {
      if (c < 0) { inner.z0 = Math.max(inner.z0, w.a.z1); outer.z0 = Math.max(outer.z0, w.a.z0); }
      else { inner.z1 = Math.min(inner.z1, w.a.z0); outer.z1 = Math.min(outer.z1, w.a.z1); }
    }
    dressed.push({ index: w.i, box: w.a, planes: [{ axis, sign }, { axis: 1, sign: 1 }], wall: true, zones: zones.get(w.i) ?? [] });
  }
  const others = rest.filter((e) => !walls.includes(e));
  for (const e of others) {
    // stair steps buried inside a mesa: nothing to dress
    if (others.some((o) => o !== e && contains(o.a, e.a))) continue;
    // grey cover crates (auto-enriched) stay crates
    if (!earthy(e.b.color)) continue;
    dressed.push({
      index: e.i, box: e.a, wall: false, zones: zones.get(e.i) ?? [],
      planes: [{ axis: 0, sign: 1 }, { axis: 0, sign: -1 }, { axis: 2, sign: 1 }, { axis: 2, sign: -1 }, { axis: 1, sign: 1 }],
    });
  }
  return { floorTop, inner, outer, dressed, solids: rest.map((e) => e.a), all: solid.map((e) => e.a) };
}

// ---------------------------------------------------------------------------
// 1. ROCK SHELLS
/** outward displacement of a vertical face at world point (x,y,z). */
export function sideDisp(x: number, y: number, z: number): number {
  const warp = (vnoise(x * 0.035, 0.5, z * 0.035, 7) - 0.5) * 1.4;
  const yy = y + warp;
  const t = yy / 0.62 + 0.18 * Math.sin(yy * 0.9);
  const band = Math.floor(t), f = t - band;
  const hard = hashI(band, 3, 1, 13);
  // each bed: recessed (soft, undercut) at its base, swelling to a caprock
  // lip at its top, then a sharp drop into the next bed's undercut
  const prof = f < 0.8 ? Math.pow(f / 0.8, 0.65) : (1 - f) / 0.2;
  const strat = (0.25 + 0.75 * hard) * prof;
  const lumps = fbm(x * 0.55, y * 0.55, z * 0.55, 31);
  const flute = 1 - Math.abs(2 * vnoise(x * 0.75, y * 0.07, z * 0.75, 41) - 1);
  const v = 0.5 * strat + 0.38 * lumps + 0.22 * flute * flute - 0.08;
  return SHELL_MIN + (SHELL_MAX - SHELL_MIN) * clamp01(v);
}
export function topDisp(x: number, z: number): number {
  return TOP_MIN + (TOP_MAX - TOP_MIN) * clamp01(fbm(x * 0.7, 3.3, z * 0.7, 51) * 1.3 - 0.15);
}

function shellColor(x: number, y: number, z: number, onTop: boolean, d: number, top: number, wall: boolean, out: THREE.Color): void {
  strataColor(x, y, z, out);
  if (onTop) {
    // sun-bleached, sand-dusted caprock
    out.lerp(DUST, 0.22 + 0.2 * vnoise(x * 0.6, 1, z * 0.6, 61));
  } else {
    // relief AO: undercuts dark, caprock lips catch the light
    const dn = (d - SHELL_MIN) / (SHELL_MAX - SHELL_MIN);
    out.multiplyScalar(0.55 + 0.55 * dn);
    // desert varnish: dark streaks hanging from the rim (tall faces only)
    const streak = smooth(0.58, 0.82, vnoise(x * 1.3, y * 0.035, z * 1.3, 71));
    const hang = smooth(top - (wall ? 7 : 3), top - 0.3, y);
    out.lerp(VARNISH, streak * hang * (wall ? 0.55 : 0.3));
    // contact: darker, dust-coloured at the sand line
    const foot = 1 - smooth(0, 0.9, y);
    out.lerp(FOOT, foot * 0.35);
    out.multiplyScalar(1 - 0.18 * (1 - smooth(-0.05, 0.5, y)));
  }
  out.multiplyScalar(ROCK_GAIN);
}

/** One box's sculpted skin. Pure three.js maths. */
export function shellGeo(d: Dressed, floorTop: number, lowSpec: boolean): THREE.BufferGeometry {
  const H = lowSpec ? 0.9 : 0.45;   // horizontal grid step
  const V = lowSpec ? 0.5 : 0.25;   // vertical grid step (strata need rows)
  const b = d.box;
  const lo = [b.x0, b.y0, b.z0], hi = [b.x1, b.y1, b.z1];
  const yBase = Math.max(b.y0, floorTop) - SINK;
  const pos: number[] = [], col: number[] = [], idx: number[] = [], uvs: number[] = [];
  const P = [0, 0, 0];
  // per-box UV offset: neighbouring rocks never show the identical photo patch
  const uvOff = hashI(d.index, 7, 3, 23) * 4;
  let fu = 0, fw = 0; // current face's in-plane axes (UVs are per FACE: no corner stretch)
  const onPlane = (axis: number, sign: number): boolean => Math.abs(P[axis] - (sign > 0 ? hi[axis] : lo[axis])) < 1e-6;
  const pushVert = (): void => {
    let ox = P[0], oy = P[1], oz = P[2];
    let dSide = 0, onTop = false;
    for (const pl of d.planes) {
      if (!onPlane(pl.axis, pl.sign)) continue;
      if (pl.axis === 1) { oy += topDisp(P[0], P[2]); onTop = true; }
      else {
        let dd = sideDisp(P[0], P[1], P[2]);
        for (const z of d.zones) {
          if (z.axis !== pl.axis || z.sign !== pl.sign) continue;
          const u = P[z.axis === 0 ? 2 : 0];
          const w = smooth(0, 0.35, Math.min(u - z.u0, z.u1 - u, P[1] - z.y0, z.y1 - P[1]));
          if (w > 0) dd = dd + (Math.min(dd, BANNER_D) - dd) * w;
        }
        dSide = Math.max(dSide, dd);
        if (pl.axis === 0) ox += pl.sign * dd; else oz += pl.sign * dd;
      }
    }
    pos.push(ox, oy, oz);
    uvs.push(P[fu] / SHELL_UV + uvOff, P[fw] / SHELL_UV + uvOff * 0.61);
    shellColor(P[0], P[1], P[2], onTop && dSide === 0, dSide || SHELL_MIN, b.y1, d.wall, tmpC);
    col.push(tmpC.r, tmpC.g, tmpC.b);
  };
  for (const pl of d.planes) {
    const N = pl.axis;
    const [U, W] = N === 0 ? [2, 1] : N === 2 ? [0, 1] : [0, 2];
    const u0 = lo[U], u1 = hi[U];
    const w0 = W === 1 ? yBase : lo[W], w1 = hi[W];
    if (w1 - w0 <= 1e-3 || u1 - u0 <= 1e-3) continue;
    const nu = Math.max(1, Math.ceil((u1 - u0) / H));
    const nw = Math.max(1, Math.ceil((w1 - w0) / (W === 1 ? V : H)));
    const base = pos.length / 3;
    fu = U; fw = W;
    for (let j = 0; j <= nw; j++) for (let i = 0; i <= nu; i++) {
      P[N] = pl.sign > 0 ? hi[N] : lo[N];
      P[U] = lin(u0, u1, i, nu);
      P[W] = lin(w0, w1, j, nw);
      pushVert();
    }
    // winding: e_U × e_W must point along the outward normal
    const eps = (U + 1) % 3 === W ? 1 : -1; // Levi-Civita sign of (U,W,N)
    const ccw = eps * pl.sign > 0;
    for (let j = 0; j < nw; j++) for (let i = 0; i < nu; i++) {
      const a = base + j * (nu + 1) + i, bq = a + 1, c = a + nu + 1, dq = c + 1;
      if (ccw) idx.push(a, bq, dq, a, dq, c); else idx.push(a, dq, bq, a, c, dq);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  geo.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(idx);
  // SMOOTH normals across the shared edges (identical positions by
  // construction) → rounded-looking rims instead of a hard box crease; the
  // vertices themselves stay per-face so each face keeps its own planar UVs
  const posOnly = new THREE.BufferGeometry();
  posOnly.setAttribute("position", geo.getAttribute("position").clone());
  posOnly.setIndex(idx);
  const welded = mergeVertices(posOnly, 1e-4);
  posOnly.dispose();
  welded.computeVertexNormals();
  const wp = welded.getAttribute("position"), wn = welded.getAttribute("normal");
  const key = (x: number, y: number, z: number): string => `${Math.round(x * 1e4)},${Math.round(y * 1e4)},${Math.round(z * 1e4)}`;
  const nmap = new Map<string, number>();
  for (let i = 0; i < wp.count; i++) nmap.set(key(wp.getX(i), wp.getY(i), wp.getZ(i)), i);
  const nor = new Float32Array(pos.length);
  for (let i = 0; i < pos.length / 3; i++) {
    const w = nmap.get(key(pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]));
    if (w === undefined) { nor[i * 3 + 1] = 1; continue; }
    nor[i * 3] = wn.getX(w); nor[i * 3 + 1] = wn.getY(w); nor[i * 3 + 2] = wn.getZ(w);
  }
  welded.dispose();
  geo.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
  return geo;
}

// ---------------------------------------------------------------------------
// 2. BACKDROP — tiered cliffs beyond the walls' outer faces
export const BACKDROP_REACH = 62;
/** height of the backdrop at a point OUTSIDE the outer rectangle. */
export function backdropHeight(x: number, z: number, cls: CanyonClass): number {
  const o = cls.outer;
  const dx = Math.max(o.x0 - x, x - o.x1), dz = Math.max(o.z0 - z, z - o.z1);
  const e = dx > 0 && dz > 0 ? Math.hypot(dx, dz) : Math.max(dx, dz);
  const rimTop = 10; // wall top on this map; refined by the caller via cls
  if (e <= 0) return rimTop - 0.1;
  const ramp = smooth(0.9, 7, e);
  const big = fbm(x * 0.016, 0.3, z * 0.016, 91, 3);
  const ridge = 1 - Math.abs(2 * vnoise(x * 0.03, 1.7, z * 0.03, 93) - 1);
  const buttes = fbm(x * 0.055, 0.9, z * 0.055, 95, 2);
  const plateau = ramp * (4 + 20 * big + 8 * ridge * ridge + 7 * buttes);
  // terraces: flat treads, steep risers — the stepped-mesa read
  const step = 2.8, t = plateau / step, k = Math.floor(t), f = t - k;
  const h = (k + smooth(0.6, 0.97, f)) * step;
  return rimTop - 0.1 + h + 0.35 * vnoise(x * 0.4, 2.1, z * 0.4, 97) * ramp;
}

function axisCoords(innerLo: number, innerHi: number, reach: number, coarse: number, lowSpec: boolean): number[] {
  const out: number[] = [];
  const n = Math.max(1, Math.round((innerHi - innerLo) / coarse));
  for (let i = 0; i <= n; i++) out.push(lin(innerLo, innerHi, i, n));
  let s = lowSpec ? 1.6 : 0.8, x = 0;
  const outside: number[] = [];
  while (x < reach) { x += s; outside.push(Math.min(x, reach)); s *= lowSpec ? 1.18 : 1.1; }
  for (const d of outside) { out.push(innerHi + d); out.unshift(innerLo - d); }
  return out;
}

export function backdropGeo(cls: CanyonClass, wallTop: number, lowSpec: boolean): THREE.BufferGeometry {
  const o = cls.outer;
  const xs = axisCoords(o.x0, o.x1, BACKDROP_REACH, lowSpec ? 6 : 3, lowSpec);
  const zs = axisCoords(o.z0, o.z1, BACKDROP_REACH, lowSpec ? 6 : 3, lowSpec);
  const nx = xs.length, nz = zs.length;
  const pos = new Float32Array(nx * nz * 3);
  const lift = wallTop - 10;
  for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
    const k = (j * nx + i) * 3;
    pos[k] = xs[i]; pos[k + 2] = zs[j];
    pos[k + 1] = backdropHeight(xs[i], zs[j], cls) + lift;
  }
  const idx: number[] = [];
  for (let j = 0; j < nz - 1; j++) for (let i = 0; i < nx - 1; i++) {
    const cx = (xs[i] + xs[i + 1]) / 2, cz = (zs[j] + zs[j + 1]) / 2;
    if (cx > o.x0 && cx < o.x1 && cz > o.z0 && cz < o.z1) continue; // the playable box
    const a = j * nx + i, b = a + 1, c = a + nx, d = c + 1;
    idx.push(a, c, b, b, c, d); // CCW from above
  }
  let geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geo.setIndex(idx);
  // drop the unused interior vertices
  const ng = geo.toNonIndexed();
  geo.dispose();
  geo = mergeVertices(ng, 1e-4);
  ng.dispose();
  geo.computeVertexNormals();
  const p = geo.getAttribute("position"), n = geo.getAttribute("normal");
  const col = new Float32Array(p.count * 3);
  for (let v = 0; v < p.count; v++) {
    const x = p.getX(v), y = p.getY(v), z = p.getZ(v);
    strataColor(x, y, z, tmpC);
    const flat = smooth(0.62, 0.86, n.getY(v));
    // treads carry sand + sparse dark scrub; risers keep the bedding colour
    tmpC2.copy(DUST).multiplyScalar(0.92 + 0.16 * vnoise(x * 0.3, 0, z * 0.3, 101));
    const scrub = smooth(0.66, 0.8, vnoise(x * 0.9, 0.5, z * 0.9, 103));
    tmpC2.multiplyScalar(1 - 0.4 * scrub);
    tmpC.lerp(tmpC2, flat * 0.35);
    // risers: desert varnish curtains
    const streak = smooth(0.55, 0.85, vnoise(x * 0.35, y * 0.02, z * 0.35, 107));
    tmpC.lerp(VARNISH, streak * (1 - flat) * 0.4);
    tmpC.multiplyScalar(ROCK_GAIN * 0.85);
    col[v * 3] = tmpC.r; col[v * 3 + 1] = tmpC.g; col[v * 3 + 2] = tmpC.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
  geo.setAttribute("uv", new THREE.BufferAttribute(new Float32Array(p.count * 2), 2));
  // big faces seen from 10-60 m: the 2.4 m scan repeat would read as a grid
  worldProjectUV(geo, 4.2);
  return geo;
}

// ---------------------------------------------------------------------------
// 3. SAND DRIFTS — straight strips against faces + quarter fans at convex corners
const driftAmp = (x: number, z: number): number =>
  DRIFT_MAX * clamp01(0.15 + 1.1 * (fbm(x * 0.22, 9.1, z * 0.22, 111, 2) - 0.32) / 0.5);

interface Acc { pos: number[]; idx: number[]; spawns: number[][] }
/** drifts flatten out around floor spawns (nobody spawns knee-deep in sand) */
function spawnAtt(acc: Acc, x: number, z: number): number {
  let a = 1;
  for (const p of acc.spawns) a = Math.min(a, smooth(0.5, SPAWN_CLEAR + 0.8, Math.hypot(p[0] - x, p[2] - z)));
  return a;
}
/** strip from (ax,az)→(bx,bz) along a face, extending `w` along (nx,nz) */
function driftStrip(acc: Acc, ax: number, az: number, bx: number, bz: number, nx: number, nz: number, w: number, y0: number, step: number): void {
  const L = Math.hypot(bx - ax, bz - az);
  const nu = Math.max(1, Math.ceil(L / step)), nw = 4;
  const base = acc.pos.length / 3;
  for (let j = 0; j <= nw; j++) for (let i = 0; i <= nu; i++) {
    const fx = lin(ax, bx, i, nu), fz = lin(az, bz, i, nu);
    const e = (j / nw) * w;
    const prof = Math.pow(1 - j / nw, 1.7);
    const px = fx + nx * e, pz = fz + nz * e;
    acc.pos.push(px, y0 + driftAmp(fx, fz) * prof * spawnAtt(acc, px, pz) - 0.04, pz);
  }
  // CCW from above: (along × outward) decides
  const up = (bx - ax) * nz - (bz - az) * nx; // y of along × n (sign trick)
  for (let j = 0; j < nw; j++) for (let i = 0; i < nu; i++) {
    const a = base + j * (nu + 1) + i, b = a + 1, c = a + nu + 1, d = c + 1;
    if (up < 0) acc.idx.push(a, b, d, a, d, c); else acc.idx.push(a, d, b, a, c, d);
  }
}
function driftFan(acc: Acc, cx: number, cz: number, a0: number, w: number, y0: number): void {
  const segs = 4, nw = 4;
  const base = acc.pos.length / 3;
  const amp = driftAmp(cx, cz);
  for (let j = 0; j <= nw; j++) for (let s = 0; s <= segs; s++) {
    const a = a0 + (s / segs) * (Math.PI / 2), e = (j / nw) * w;
    const px = cx + Math.cos(a) * e, pz = cz + Math.sin(a) * e;
    acc.pos.push(px, y0 + amp * Math.pow(1 - j / nw, 1.7) * spawnAtt(acc, px, pz) - 0.04, pz);
  }
  for (let j = 0; j < nw; j++) for (let s = 0; s < segs; s++) {
    const a = base + j * (segs + 1) + s, b = a + 1, c = a + segs + 1, d = c + 1;
    acc.idx.push(a, b, d, a, d, c); // CCW from above (angle runs CCW, j outward)
  }
}

export function driftGeo(cls: CanyonClass, spawns: readonly (readonly number[])[], lowSpec: boolean): THREE.BufferGeometry {
  const y0 = cls.floorTop;
  const acc: Acc = { pos: [], idx: [], spawns: spawns.filter((p) => p[1] < y0 + 0.5).map((p) => [...p]) };
  const step = lowSpec ? 1.2 : 0.6;
  const I = cls.inner;
  const WW = 2.6; // wall drifts
  driftStrip(acc, I.x0, I.z0, I.x1, I.z0, 0, 1, WW, y0, step);
  driftStrip(acc, I.x1, I.z1, I.x0, I.z1, 0, -1, WW, y0, step);
  driftStrip(acc, I.x0, I.z1, I.x0, I.z0, 1, 0, WW, y0, step);
  driftStrip(acc, I.x1, I.z0, I.x1, I.z1, -1, 0, WW, y0, step);
  for (const d of cls.dressed) {
    if (d.wall) continue;
    const b = d.box;
    if (b.y0 > y0 + 0.05) continue; // only things standing on the floor
    const w = Math.min(1.6, 0.5 + 0.12 * Math.max(b.x1 - b.x0, b.z1 - b.z0));
    driftStrip(acc, b.x0, b.z0, b.x1, b.z0, 0, -1, w, y0, step);
    driftStrip(acc, b.x1, b.z1, b.x0, b.z1, 0, 1, w, y0, step);
    driftStrip(acc, b.x0, b.z1, b.x0, b.z0, -1, 0, w, y0, step);
    driftStrip(acc, b.x1, b.z0, b.x1, b.z1, 1, 0, w, y0, step);
    driftFan(acc, b.x1, b.z1, 0, w, y0);
    driftFan(acc, b.x0, b.z1, Math.PI / 2, w, y0);
    driftFan(acc, b.x0, b.z0, Math.PI, w, y0);
    driftFan(acc, b.x1, b.z0, Math.PI * 1.5, w, y0);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(acc.pos, 3));
  geo.setIndex(acc.idx);
  geo.computeVertexNormals();
  const n = geo.getAttribute("position").count;
  // natural-ground photo shader takes the tint's HUE from vColor → the floor's sand tint
  const sand = new THREE.Color("#c8a86a");
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { col[i * 3] = sand.r; col[i * 3 + 1] = sand.g; col[i * 3 + 2] = sand.b; }
  geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
  geo.setAttribute("uv", new THREE.BufferAttribute(new Float32Array(n * 2), 2));
  worldProjectUV(geo, 6);
  return geo;
}

// ---------------------------------------------------------------------------
// 4/5. scatter: talus, tufts, sagebrush
export interface Inst { x: number; y: number; z: number; s: number; sy: number; rot: number }
export interface Scatter { talus: Inst[]; tufts: Inst[]; brush: Inst[] }

export function scatter(map: MapDef, cls: CanyonClass, lowSpec: boolean): Scatter {
  const rnd = mulberry(0xca9 + (lowSpec ? 1 : 0));
  const y0 = cls.floorTop;
  const dens = lowSpec ? 0.5 : 1;
  const spawns = map.spawns.map((s) => s.p);
  const pads = (map.jumpPads ?? []).map((p) => p.p);
  const clearOf = (x: number, z: number, r: number): boolean => {
    for (const p of spawns) if (Math.hypot(p[0] - x, p[2] - z) < SPAWN_CLEAR + r) return false;
    for (const p of pads) if (Math.hypot(p[0] - x, p[2] - z) < SPAWN_CLEAR + r) return false;
    return true;
  };
  const inSolid = (x: number, z: number, m: number): boolean => {
    for (const a of cls.solids) if (a.y1 > y0 + 0.05 && x > a.x0 - m && x < a.x1 + m && z > a.z0 - m && z < a.z1 + m) return true;
    return false;
  };
  const I = cls.inner;
  const inPlay = (x: number, z: number, m: number): boolean => x > I.x0 + m && x < I.x1 - m && z > I.z0 + m && z < I.z1 - m;
  const out: Scatter = { talus: [], tufts: [], brush: [] };
  // talus scale: worst of procedural top and scanned top must stay ≤ RUBBLE_MAX_H
  const sMaxScan = RUBBLE_MAX_H / (SCAN_SCALE * SCAN_MAX_H * (1 - SCAN_EMBED));
  const sMaxProc = RUBBLE_MAX_H / (PROC_HALF_H * (1 + PROC_LIFT));
  const SMAX = Math.min(sMaxScan, sMaxProc) * 0.98;
  type Face = { ax: number; az: number; bx: number; bz: number; nx: number; nz: number };
  const faces: { f: Face; wall: boolean }[] = [];
  faces.push({ f: { ax: I.x0, az: I.z0, bx: I.x1, bz: I.z0, nx: 0, nz: 1 }, wall: true });
  faces.push({ f: { ax: I.x0, az: I.z1, bx: I.x1, bz: I.z1, nx: 0, nz: -1 }, wall: true });
  faces.push({ f: { ax: I.x0, az: I.z0, bx: I.x0, bz: I.z1, nx: 1, nz: 0 }, wall: true });
  faces.push({ f: { ax: I.x1, az: I.z0, bx: I.x1, bz: I.z1, nx: -1, nz: 0 }, wall: true });
  for (const d of cls.dressed) {
    if (d.wall || d.box.y0 > y0 + 0.05) continue;
    const b = d.box;
    faces.push({ f: { ax: b.x0, az: b.z0, bx: b.x1, bz: b.z0, nx: 0, nz: -1 }, wall: false });
    faces.push({ f: { ax: b.x0, az: b.z1, bx: b.x1, bz: b.z1, nx: 0, nz: 1 }, wall: false });
    faces.push({ f: { ax: b.x0, az: b.z0, bx: b.x0, bz: b.z1, nx: -1, nz: 0 }, wall: false });
    faces.push({ f: { ax: b.x1, az: b.z0, bx: b.x1, bz: b.z1, nx: 1, nz: 0 }, wall: false });
  }
  for (const { f, wall } of faces) {
    const L = Math.hypot(f.bx - f.ax, f.bz - f.az);
    // TALUS: piles at the foot, bigger + denser near the face
    const nT = Math.round(L * (wall ? 0.55 : 1.3) * dens);
    for (let k = 0; k < nT; k++) {
      // clumped: cluster centres along the face
      const t = (Math.floor(rnd() * Math.max(1, L / 3)) + 0.5 + (rnd() - 0.5) * 0.9) / Math.max(1, L / 3);
      const tt = Math.min(1, Math.max(0, t));
      const r = rnd();
      const e = (wall ? 1.6 : 1.2) * r * r;
      const s = SMAX * (0.25 + 0.75 * (1 - r) * (0.45 + 0.55 * rnd()));
      const x = f.ax + (f.bx - f.ax) * tt + f.nx * (e + s * 0.4);
      const z = f.az + (f.bz - f.az) * tt + f.nz * (e + s * 0.4);
      if (!inPlay(x, z, 0.05) || !clearOf(x, z, s * SCAN_SCALE) || inSolid(x, z, -0.05)) continue;
      out.talus.push({ x, y: y0 + s * 0.9 * PROC_LIFT, z, s, sy: s * 0.9, rot: rnd() * Math.PI * 2 });
    }
    // TUFTS and BRUSH: a little further out, where wind-blown seed lodges
    const nG = Math.round(L * (wall ? 0.2 : 0.4) * dens);
    for (let k = 0; k < nG; k++) {
      const tt = rnd(), e = 0.35 + 2.4 * rnd() * rnd();
      const x = f.ax + (f.bx - f.ax) * tt + f.nx * e, z = f.az + (f.bz - f.az) * tt + f.nz * e;
      const h = 0.32 + 0.3 * rnd();
      if (!inPlay(x, z, 0.3) || !clearOf(x, z, 0.3) || inSolid(x, z, 0.25)) continue;
      out.tufts.push({ x, y: y0 - 0.02, z, s: h, sy: h, rot: rnd() * Math.PI * 2 });
    }
    const nB = Math.round(L * (wall ? 0.05 : 0.09) * dens);
    for (let k = 0; k < nB; k++) {
      const tt = rnd(), e = 0.9 + 2.5 * rnd();
      const x = f.ax + (f.bx - f.ax) * tt + f.nx * e, z = f.az + (f.bz - f.az) * tt + f.nz * e;
      const h = 0.5 + 0.35 * rnd();
      if (!inPlay(x, z, 0.6) || !clearOf(x, z, 0.6) || inSolid(x, z, 0.55)) continue;
      out.brush.push({ x, y: y0 - 0.03, z, s: h * (0.9 + 0.3 * rnd()), sy: h, rot: rnd() * Math.PI * 2 });
    }
  }
  // open-floor scatter: pebble clusters and lone grass clumps
  const nOpen = Math.round(70 * dens);
  for (let k = 0; k < nOpen; k++) {
    const cx = I.x0 + 2 + rnd() * (I.x1 - I.x0 - 4), cz = I.z0 + 2 + rnd() * (I.z1 - I.z0 - 4);
    const kind = rnd();
    const m = 1 + Math.floor(rnd() * 3);
    for (let q = 0; q < m; q++) {
      const x = cx + (rnd() - 0.5) * 1.4, z = cz + (rnd() - 0.5) * 1.4;
      if (!inPlay(x, z, 0.3) || !clearOf(x, z, 0.4) || inSolid(x, z, 0.4)) continue;
      if (kind < 0.45) { const s = SMAX * (0.15 + 0.3 * rnd()); out.talus.push({ x, y: y0 + s * 0.9 * PROC_LIFT, z, s, sy: s * 0.9, rot: rnd() * 6.283 }); }
      else if (kind < 0.9) { const h = 0.28 + 0.3 * rnd(); out.tufts.push({ x, y: y0 - 0.02, z, s: h, sy: h, rot: rnd() * 6.283 }); }
      else if (q === 0) { const h = 0.5 + 0.3 * rnd(); out.brush.push({ x, y: y0 - 0.03, z, s: h, sy: h, rot: rnd() * 6.283 }); }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// instance geometries (unit-sized; instances scale them)
/** procedural talus rock: radius 1, half-height PROC_HALF_H, centred */
export function talusGeo(): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(1, 1);
  const p = g.getAttribute("position");
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const k = 0.78 + 0.22 * vnoise(x * 2.1 + 5, y * 2.1, z * 2.1, 131);
    // flat-ish base + top: broken slab, not a ball
    p.setXYZ(i, x * k, Math.max(-0.85, Math.min(0.85, y * k)) * (PROC_HALF_H / 0.85), z * k);
  }
  g.computeVertexNormals();
  return g;
}

/** bunchgrass: curved tapering blades, unit height. */
export function tuftGeo(): THREE.BufferGeometry {
  const rnd = mulberry(77);
  const pos: number[] = [], nor: number[] = [], col: number[] = [], idx: number[] = [];
  const BASE = new THREE.Color("#5e4c30"), TIP = new THREE.Color("#dcc792"), SAGE = new THREE.Color("#a5a578");
  const blades = 13;
  for (let b = 0; b < blades; b++) {
    const a = rnd() * Math.PI * 2, tilt = 0.15 + rnd() * 0.65, len = 0.65 + 0.35 * rnd();
    const ca = Math.cos(a), sa = Math.sin(a);
    const tipCol = tmpC2.copy(TIP).lerp(SAGE, rnd() * 0.5).clone();
    const base = pos.length / 3;
    const SEG = 3;
    for (let k = 0; k <= SEG; k++) {
      const t = k / SEG;
      const lean = tilt * (0.4 + t * 1.2); // droops more toward the tip
      const r = 0.04 + Math.sin(lean) * t * len * 0.75;
      const y = Math.min(1, Math.cos(lean * 0.9) * t * len);
      const w = 0.028 * (1 - t) + 0.002;
      const cx = ca * r, cz = sa * r;
      pos.push(cx - sa * w, y, cz + ca * w, cx + sa * w, y, cz - ca * w);
      const nx = ca * 0.45, nz = sa * 0.45;
      nor.push(nx, 0.89, nz, nx, 0.89, nz);
      tmpC.copy(BASE).lerp(tipCol, Math.pow(t, 0.7));
      col.push(tmpC.r, tmpC.g, tmpC.b, tmpC.r, tmpC.g, tmpC.b);
    }
    for (let k = 0; k < SEG; k++) {
      const i0 = base + k * 2;
      idx.push(i0, i0 + 1, i0 + 3, i0, i0 + 3, i0 + 2);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  windGeometry(g, (_x, y) => uprightSway(y, 0, 1));
  return g;
}

/** sagebrush: a gnarled twig skeleton with grey-green / dried leaf clumps, unit height. */
export function brushGeo(): THREE.BufferGeometry {
  const rnd = mulberry(91);
  const pos: number[] = [], col: number[] = [], idx: number[] = [];
  const BARK = new THREE.Color("#5a4a3c"), LEAF = new THREE.Color("#8e9470"), DRY = new THREE.Color("#a08a62");
  const v = new THREE.Vector3(), t1 = new THREE.Vector3(), t2 = new THREE.Vector3();
  const prism = (a: THREE.Vector3, b: THREE.Vector3, ra: number, rb: number): void => {
    v.subVectors(b, a).normalize();
    t1.set(Math.abs(v.y) < 0.9 ? 0 : 1, Math.abs(v.y) < 0.9 ? 1 : 0, 0).cross(v).normalize();
    t2.crossVectors(v, t1);
    const base = pos.length / 3;
    for (const [p, r] of [[a, ra], [b, rb]] as const) for (let k = 0; k < 3; k++) {
      const an = (k / 3) * Math.PI * 2;
      pos.push(p.x + (t1.x * Math.cos(an) + t2.x * Math.sin(an)) * r, p.y + (t1.y * Math.cos(an) + t2.y * Math.sin(an)) * r, p.z + (t1.z * Math.cos(an) + t2.z * Math.sin(an)) * r);
      col.push(BARK.r, BARK.g, BARK.b);
    }
    for (let k = 0; k < 3; k++) {
      const k1 = (k + 1) % 3;
      idx.push(base + k, base + k1, base + 3 + k1, base + k, base + 3 + k1, base + 3 + k);
    }
  };
  const blob = (c: THREE.Vector3, r: number): void => {
    const base = pos.length / 3;
    const dry = rnd() < 0.35;
    tmpC.copy(dry ? DRY : LEAF).multiplyScalar(0.85 + 0.3 * rnd());
    const pts = [[1, 0, 0], [-1, 0, 0], [0, 0.75, 0], [0, -0.6, 0], [0, 0, 1], [0, 0, -1]];
    for (const [x, y, z] of pts) {
      const j = 0.75 + 0.5 * rnd();
      pos.push(c.x + x * r * j, c.y + y * r * j, c.z + z * r * j);
      col.push(tmpC.r, tmpC.g, tmpC.b);
    }
    const f = [[0, 2, 4], [4, 2, 1], [1, 2, 5], [5, 2, 0], [4, 3, 0], [1, 3, 4], [5, 3, 1], [0, 3, 5]];
    for (const [a, b, c2] of f) idx.push(base + a, base + b, base + c2);
  };
  const branch = (a: THREE.Vector3, dir: THREE.Vector3, len: number, r: number, depth: number): void => {
    const b = a.clone().addScaledVector(dir, len);
    prism(a, b, r, r * 0.6);
    if (depth >= 2) {
      blob(b, 0.11 + 0.06 * rnd());
      blob(a.clone().lerp(b, 0.6).add(new THREE.Vector3((rnd() - 0.5) * 0.08, 0, (rnd() - 0.5) * 0.08)), 0.08 + 0.05 * rnd());
      return;
    }
    const kids = depth === 0 ? 3 : 2;
    for (let k = 0; k < kids; k++) {
      const d = dir.clone().add(new THREE.Vector3((rnd() - 0.5) * 0.9, 0.15 + rnd() * 0.25, (rnd() - 0.5) * 0.9)).normalize();
      branch(b, d, len * (0.6 + 0.2 * rnd()), r * 0.6, depth + 1);
    }
  };
  const stems = 4;
  for (let s = 0; s < stems; s++) {
    const a = (s / stems) * Math.PI * 2 + rnd() * 0.8;
    const dir = new THREE.Vector3(Math.cos(a) * 0.45, 1, Math.sin(a) * 0.45).normalize();
    branch(new THREE.Vector3(Math.cos(a) * 0.04, 0, Math.sin(a) * 0.04), dir, 0.32 + 0.1 * rnd(), 0.025, 0);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  // normalise to unit height, base at 0
  g.computeBoundingBox();
  const bb = g.boundingBox!;
  g.translate(0, -bb.min.y, 0);
  g.scale(1 / (bb.max.y - bb.min.y), 1 / (bb.max.y - bb.min.y), 1 / (bb.max.y - bb.min.y));
  g.computeVertexNormals();
  g.computeBoundingBox();
  windGeometry(g, (_x, y) => uprightSway(y, 0.15, 1));
  return g;
}

// ---------------------------------------------------------------------------
export interface CanyonParts {
  cls: CanyonClass;
  shells: { d: Dressed; geo: THREE.BufferGeometry }[];
  backdrop: THREE.BufferGeometry;
  drifts: THREE.BufferGeometry;
  scatter: Scatter;
}

/** Everything geometric, no materials/textures (the bench drives this). */
export function canyonParts(map: MapDef, lowSpec: boolean): CanyonParts {
  const cls = classify(map);
  const shells = cls.dressed.map((d) => ({ d, geo: shellGeo(d, cls.floorTop, lowSpec) }));
  const wallTop = Math.max(...cls.dressed.filter((d) => d.wall).map((d) => d.box.y1), 10);
  return { cls, shells, backdrop: backdropGeo(cls, wallTop, lowSpec), drifts: driftGeo(cls, map.spawns.map((s) => s.p), lowSpec), scatter: scatter(map, cls, lowSpec) };
}

const m4 = new THREE.Matrix4(), q4 = new THREE.Quaternion(), v3 = new THREE.Vector3(), s3 = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0);
function instanced(geo: THREE.BufferGeometry, mat: THREE.Material, list: Inst[], name: string): THREE.InstancedMesh {
  const im = new THREE.InstancedMesh(geo, mat, list.length);
  list.forEach((it, i) => {
    q4.setFromAxisAngle(up, it.rot);
    m4.compose(v3.set(it.x, it.y, it.z), q4, s3.set(it.s, it.sy, it.s));
    im.setMatrixAt(i, m4);
  });
  im.instanceMatrix.needsUpdate = true;
  im.computeBoundingSphere();
  im.name = name;
  return im;
}

// ---------------------------------------------------------------------------
// 6. ARCHITECTURE STRIP — the generic renderer passes (mapDressing.ts) treat
// every tall canyon box as a BUILDING: cornices, quoins, plinths, string
// courses, roof decks with skylights, even shopfronts with awnings and lit
// signs on the canyon walls. On real rock that is nonsense, and those pieces
// stand up to ~0.18 m proud — through the rock skin. When this group is added
// to the map group (the renderer adds every dressing mesh BEFORE the extras),
// triangles of the earlier static meshes that float OUTSIDE every collider in
// the strip zone of a dressed rock are dropped from their index. The colliders'
// own surfaces (inside-or-on a box) are always kept, so this can only remove
// decoration, never a wall you can touch.
export const STRIP_REACH = 3.2;
const KEEP_EPS = 0.004;
export function strippable(x: number, y: number, z: number, cls: CanyonClass): boolean {
  if (y < cls.floorTop + 0.04) return false; // floor decals / contact shadow stay
  for (const a of cls.all) {
    if (x >= a.x0 - KEEP_EPS && x <= a.x1 + KEEP_EPS && y >= a.y0 - KEEP_EPS && y <= a.y1 + KEEP_EPS && z >= a.z0 - KEEP_EPS && z <= a.z1 + KEEP_EPS) return false;
  }
  for (const d of cls.dressed) {
    const b = d.box;
    if (y > b.y1 + 1.2 || y < b.y0 - 0.1) continue;
    const ox = Math.max(b.x0 - x, x - b.x1, 0), oz = Math.max(b.z0 - z, z - b.z1, 0);
    if (Math.max(ox, oz) <= (d.wall ? STRIP_REACH : 1.2)) return true;
  }
  return false;
}
/** Drop strippable triangles from one geometry (load time). Returns count removed. */
export function stripGeometry(geo: THREE.BufferGeometry, cls: CanyonClass): number {
  const pos = geo.getAttribute("position");
  if (!pos) return 0;
  const index = geo.index;
  const n = index ? index.count : pos.count;
  const keep: number[] = [];
  let removed = 0;
  for (let t = 0; t + 2 < n; t += 3) {
    const a = index ? index.getX(t) : t, b = index ? index.getX(t + 1) : t + 1, c = index ? index.getX(t + 2) : t + 2;
    const x = (pos.getX(a) + pos.getX(b) + pos.getX(c)) / 3;
    const y = (pos.getY(a) + pos.getY(b) + pos.getY(c)) / 3;
    const z = (pos.getZ(a) + pos.getZ(b) + pos.getZ(c)) / 3;
    if (strippable(x, y, z, cls)) removed++;
    else keep.push(a, b, c);
  }
  if (removed) geo.setIndex(keep);
  return removed;
}
function stripSiblings(root: THREE.Object3D, cls: CanyonClass): void {
  const parent = root.parent;
  if (!parent) return;
  let removed = 0;
  for (const o of parent.children) {
    if (o === root) continue;
    const m = o as THREE.Mesh;
    // only the renderer's merged static meshes: never instanced decor, prop
    // swaps, lines or sprites
    if (!m.isMesh || (m as THREE.InstancedMesh).isInstancedMesh || m.userData.propSwap || !m.geometry) continue;
    if (m.matrixWorld && !m.matrix.equals(IDENTITY)) continue; // world-space geometry only
    removed += stripGeometry(m.geometry, cls);
    const idx = m.geometry.index;
    if (idx && idx.count === 0) m.visible = false;
  }
  if (removed) console.log(`[canyon extras] stripped ${removed} architectural-dressing triangles off the rock`);
}
const IDENTITY = new THREE.Matrix4();

export function build(ctx: MapExtrasContext): THREE.Object3D | null {
  const parts = canyonParts(ctx.map, ctx.lowSpec);
  const root = new THREE.Group();
  root.name = "extras:canyon";

  // ROCK: one material for shells + backdrop (upgraded to the photo rock scan
  // family by pbrTextures when it loads; vertex colour carries the strata)
  const rockMat = new THREE.MeshStandardMaterial(surfaceMaps("boulder", { color: 0xffffff, vertexColors: true, roughness: 0.95, metalness: 0 }, 1.2));
  const shellMerged = mergeGeometries(parts.shells.map((s) => s.geo));
  for (const s of parts.shells) s.geo.dispose();
  if (shellMerged) {
    shellMerged.computeBoundingSphere();
    const m = new THREE.Mesh(shellMerged, rockMat);
    m.name = "cy:shells";
    m.castShadow = false;   // the collider box under it already casts the same shadow
    m.receiveShadow = true;
    root.add(m);
  }
  const back = new THREE.Mesh(parts.backdrop, rockMat);
  back.name = "cy:backdrop";
  back.castShadow = false;
  back.receiveShadow = true;
  root.add(back);

  // SAND: the floor's own natural-ground photo family
  const sandMat = new THREE.MeshStandardMaterial(surfaceMaps("terrain", { color: 0xffffff, vertexColors: true, roughness: 1, metalness: 0 }, 1));
  sandMat.userData.npPbrFamily = "sand";
  sandMat.polygonOffset = true;
  sandMat.polygonOffsetFactor = -1;
  sandMat.polygonOffsetUnits = -1;
  const drifts = new THREE.Mesh(parts.drifts, sandMat);
  drifts.name = "cy:drifts";
  drifts.receiveShadow = true;
  root.add(drifts);

  // TALUS: tagged so mapProps swaps in the photoscanned rocks
  const sc = parts.scatter;
  if (sc.talus.length) {
    const tm = new THREE.MeshStandardMaterial(surfaceMaps("boulder", { color: "#b98a62", roughness: 0.95, metalness: 0 }, 1));
    const im = instanced(talusGeo(), tm, sc.talus, "cy:talus");
    im.userData.propKind = "rock";
    im.userData.propLift = PROC_LIFT;
    im.castShadow = ctx.shadows;
    im.receiveShadow = true;
    root.add(im);
  }
  // VEGETATION: one wind material, two instanced meshes
  const vegMat = applyWind(new THREE.MeshStandardMaterial({ color: 0xffffff, vertexColors: true, roughness: 0.92, metalness: 0, side: THREE.DoubleSide }), 0.07, 1.4);
  if (sc.tufts.length) {
    const im = instanced(tuftGeo(), vegMat, sc.tufts, "cy:tufts");
    im.receiveShadow = true;
    root.add(im);
  }
  if (sc.brush.length) {
    const im = instanced(brushGeo(), vegMat, sc.brush, "cy:brush");
    im.receiveShadow = true;
    root.add(im);
  }
  for (const o of root.children) { o.matrixAutoUpdate = false; o.updateMatrix(); }
  const onAdded = (): void => {
    root.removeEventListener("added", onAdded);
    try { stripSiblings(root, parts.cls); } catch (e) { console.error("[canyon extras] strip failed", e); }
  };
  root.addEventListener("added", onAdded);
  return root;
}
